import React, { useEffect, useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import Modal from '../../../components/ui/Modal';
import Btn from '../../../components/ui/Btn';
import Field, { TextInput } from '../../../components/ui/Field';

export const KEY_RE = /^sk-[A-Za-z0-9_-]{20,255}$/;
export const agentsText = (n) => (n === 1 ? '1 agente' : `${n} agentes`);

// Editar una clave: su nombre, la clave nueva (cuando caduca o cambia) y si es pública. La clave actual nunca se muestra; la nueva
// reemplaza a la vieja para TODOS los agentes que la usan. Solo se manda lo que cambió.
export default function CredentialEditModal({ open, onClose, credential, onSave }) {
  const [label, setLabel] = useState('');
  const [secret, setSecret] = useState('');
  const [show, setShow] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open || !credential) return;
    setLabel(credential.label || ''); setSecret(''); setShow(false); setIsPublic(Boolean(credential.is_public)); setError(''); setTouched(false); setBusy(false);
  }, [open, credential]);

  if (!credential) return null;
  const users = credential.agent_count || 0;
  const shared = users > 1;
  const labelError = touched && !label.trim() ? 'Ponle un nombre para reconocerla.' : '';
  const secretError = touched && secret.trim() && !KEY_RE.test(secret.trim()) ? 'La clave empieza con «sk-» y no lleva espacios.' : '';
  const lockedPublic = credential.is_public && shared;       // no puede volver a ser privada mientras la usen varios agentes
  const names = (credential.agents || []).slice(0, 5).map((a) => `${a.name}${a.project_name ? ` (${a.project_name})` : ''}`);
  const more = (credential.agents || []).length - names.length;

  const submit = async (e) => {
    e?.preventDefault();
    setTouched(true);
    if (!label.trim() || (secret.trim() && !KEY_RE.test(secret.trim()))) return;
    const patch = {};
    if (label.trim() !== credential.label) patch.label = label.trim();
    if (secret.trim()) patch.secret = secret.trim();
    if (isPublic !== Boolean(credential.is_public)) patch.isPublic = isPublic;
    if (!Object.keys(patch).length) { onClose(); return; }
    setBusy(true); setError('');
    try { await onSave(credential.id, patch); onClose(); }
    catch (err) { setError(err.message); setBusy(false); }
  };

  return (
    <Modal open={open} onClose={busy ? undefined : onClose} title="Editar la clave" description={`Termina en ${credential.last4}. Por seguridad, la clave completa no se puede volver a ver.`}
      footer={<>
        <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Btn>
        <Btn variant="primary" onClick={submit} loading={busy}>Guardar cambios</Btn>
      </>}>
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field label="Nombre de la clave" error={labelError}>
          <TextInput value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} autoComplete="off" data-autofocus />
        </Field>
        <Field label="Clave nueva de OpenAI" optional error={secretError} hint="Pégala solo si la anterior caducó o cambió. Se guarda cifrada y reemplaza a la actual."
          adornment={
            <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? 'Ocultar la clave' : 'Mostrar la clave'}
              className="h-8 w-8 grid place-items-center rounded-md text-fg-faint hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60">
              {show ? <FaEyeSlash aria-hidden /> : <FaEye aria-hidden />}
            </button>}>
          <TextInput type={show ? 'text' : 'password'} value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="sk-…" autoComplete="off" spellCheck={false} className="pr-10 tabular-nums" />
        </Field>

        <label className={`flex items-start gap-3 text-[13.5px] ${lockedPublic ? 'opacity-70' : 'cursor-pointer'}`}>
          <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} disabled={lockedPublic}
            className="mt-0.5 h-4 w-4 accent-[rgb(var(--live))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/70" />
          <span>
            <span className="font-medium text-fg">Que sea pública</span>
            <span className="block text-[12.5px] text-fg-dim">
              {lockedPublic ? `La usan ${agentsText(users)}: quítasela a todos menos uno para volver a hacerla privada.` : 'Cualquiera del equipo podrá asignarla a los agentes de pruebas de concepto. Nadie ve la clave.'}
            </span>
          </span>
        </label>

        {users > 0 && (
          <div role="note" className={`rounded-lg px-3 py-2 text-[12.5px] leading-relaxed ${shared && secret.trim() ? 'border border-caution/30 bg-caution-soft text-fg' : 'bg-raised text-fg-dim'}`}>
            <b>La usa{users === 1 ? '' : 'n'} {agentsText(users)}:</b> {names.join(', ')}{more > 0 ? ` y ${more} más` : ''}.
            {shared && secret.trim() ? ' Al guardar, todos usarán la clave nueva.' : ''}
          </div>
        )}
        {error && <p role="alert" className="text-sm text-signal">{error}</p>}
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}
